(ns arthur.flow.condition.eyes
  "Condition measured eyes while preserving every source frame. Lid geometry
  stays dense; blink and shared gaze are decisions over those measurements."
  (:require [arthur.domain.ring :as ring]
            [arthur.flow.condition :as condition]))

(defn- distance [a b]
  (js/Math.hypot (- (:x a) (:x b)) (- (:y a) (:y b))))

(defn- socket [lid]
  (let [a (first lid) b (nth lid (quot (count lid) 2))]
    {:x (/ (+ (:x a) (:x b)) 2)
     :y (/ (+ (:y a) (:y b)) 2)
     :w (distance a b)}))

(defn- hold-observed
  "Give temporal filters real samples at every index. The freeze mask still
  marks the gap absent; these held values are only numeric placeholders."
  [values observed fallback]
  (let [values (vec values)
        seed (or (first (keep-indexed (fn [i v]
                                       (when (and (nth observed i) (some? v)) v))
                                     values))
                 fallback (first values))]
    (loop [f 0 last-value seed out []]
      (if (= f (count values))
        out
        (let [v (nth values f)
              next-value (if (and (nth observed f) (some? v)) v last-value)]
          (recur (inc f) next-value (conj out next-value)))))))

(defn- mean-width [lids observed]
  (let [valid (seq (keep-indexed (fn [i lid] (when (nth observed i) lid)) lids))
        rings (or valid lids)]
    (/ (reduce + (map (comp :w socket) rings)) (count rings))))

(defn apply-defaults
  [{:keys [fps contour-avg blink-cut gaze-gain gaze-step iris-size
           lash-weight pupil-size]} {:keys [lid-r lid-l open-r open-l gaze
                                            observed-r observed-l gaze-observed]
                                    :as measured}]
  (let [fps (or fps 30)
        observed-r (or observed-r (vec (repeat (count lid-r) true)))
        observed-l (or observed-l (vec (repeat (count lid-l) true)))
        gaze-observed (or gaze-observed (vec (repeat (count gaze) true)))
        right (condition/contours {:contour-avg contour-avg}
                                  (hold-observed lid-r observed-r nil))
        left (condition/contours {:contour-avg contour-avg}
                                 (hold-observed lid-l observed-l nil))
        w-r (mean-width right observed-r)
        w-l (mean-width left observed-l)
        w (/ (+ w-r w-l) 2)
        valid-gaze (seq (keep-indexed (fn [i point]
                                       (when (nth gaze-observed i) point)) gaze))
        gaze (hold-observed gaze gaze-observed {:x 0 :y 0})
        rest (if valid-gaze (condition/median-point valid-gaze) {:x 0 :y 0})
        px (mapv (fn [{:keys [x y]}]
                   {:x (* (- x (:x rest)) gaze-gain w)
                    :y (* (- y (:y rest)) gaze-gain w)}) gaze)
        cells (condition/quantize-snap px (* gaze-step w)
                                       (js/Math.round (* fps 0.08)))
        centres (fn [lids]
                  (mapv (fn [lid shift]
                          (let [{:keys [x y]} (socket lid)]
                            [ (+ x (:x shift)) (+ y (:y shift)) ]))
                        lids cells))
        blink {:cut blink-cut :dwell 0 :hold (max 2 (js/Math.ceil (* fps 0.1)))}]
    (assoc measured
           :lid-r right :lid-l left
           :lash-r (mapv #(ring/offset-ring % (* lash-weight w-r)) right)
           :lash-l (mapv #(ring/offset-ring % (* lash-weight w-l)) left)
           :shut-r (condition/resolve-blink
                    (hold-observed open-r observed-r nil) blink)
           :shut-l (condition/resolve-blink
                    (hold-observed open-l observed-l nil) blink)
           :iris-r (centres right) :iris-l (centres left)
           :radius-r (* 0.5 iris-size w-r)
           :radius-l (* 0.5 iris-size w-l)
           :pupil-size (* pupil-size w))))
